import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Monitor, Smartphone, Tablet, ExternalLink, RefreshCw } from "lucide-react";
import { ContentBlock } from "./ContentEditor";
import { sanitizeHTML } from "@/utils/htmlSanitization";

interface LivePreviewProps {
  contentData: any;
  tenantSlug: string;
  pageSlug: string;
  referrerMessage: string;
}

export default function LivePreview({ contentData, tenantSlug, pageSlug, referrerMessage }: LivePreviewProps) {
  const [viewMode, setViewMode] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [refreshKey, setRefreshKey] = useState(0);

  const getViewportClasses = () => {
    switch (viewMode) {
      case 'mobile':
        return 'w-full max-w-sm mx-auto';
      case 'tablet':
        return 'w-full max-w-2xl mx-auto';
      case 'desktop':
      default:
        return 'w-full';
    }
  };

  const getViewportWidth = () => {
    switch (viewMode) {
      case 'mobile':
        return '375px';
      case 'tablet':
        return '768px';
      case 'desktop':
      default:
        return '100%';
    }
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleOpenInNewTab = () => {
    const url = `${window.location.origin}/lp/${tenantSlug}/${pageSlug}`;
    window.open(url, '_blank');
  };

  return (
    <div className="space-y-4">
      {/* Preview Controls */}
      <div className="flex justify-between items-center">
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'desktop' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('desktop')}
          >
            <Monitor className="h-4 w-4 mr-2" />
            Desktop
          </Button>
          <Button
            variant={viewMode === 'tablet' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('tablet')}
          >
            <Tablet className="h-4 w-4 mr-2" />
            Tablet
          </Button>
          <Button
            variant={viewMode === 'mobile' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('mobile')}
          >
            <Smartphone className="h-4 w-4 mr-2" />
            Mobile
          </Button>
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Aktualisieren
          </Button>
          <Button variant="outline" size="sm" onClick={handleOpenInNewTab}>
            <ExternalLink className="h-4 w-4 mr-2" />
            In neuem Tab öffnen
          </Button>
        </div>
      </div>

      {/* Preview Frame */}
      <Card>
        <CardContent className="p-4">
          <div className={`${getViewportClasses()} transition-all duration-300`}>
            <div 
              className="border rounded-lg overflow-hidden bg-white shadow-sm"
              style={{ width: getViewportWidth(), minHeight: '600px' }}
            >
              <PreviewContent 
                key={refreshKey}
                contentData={contentData}
                referrerMessage={referrerMessage}
                viewMode={viewMode}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

interface PreviewContentProps {
  contentData: any;
  referrerMessage: string;
  viewMode: 'desktop' | 'tablet' | 'mobile';
}

function PreviewContent({ contentData, referrerMessage, viewMode }: PreviewContentProps) {
  const blocks = contentData?.blocks || [];
  const visibleBlocks = blocks
    .filter((block: ContentBlock) => block.settings.isVisible !== false)
    .sort((a: ContentBlock, b: ContentBlock) => a.order - b.order);

  return (
    <div className="min-h-full">
      {/* Referrer Message */}
      {referrerMessage && (
        <div className="bg-primary text-primary-foreground p-3 text-center text-sm">
          {referrerMessage.replace('{referrer_name}', 'Max Mustermann')}
        </div>
      )}

      {/* Content Blocks */}
      {visibleBlocks.map((block: ContentBlock) => (
        <div
          key={block.id}
          className={`${block.settings.padding || 'py-16'}`}
          style={{
            backgroundColor: block.settings.backgroundColor,
            color: block.settings.textColor
          }}
        >
          {block.type === 'hero' && (
            <PreviewHeroBlock content={block.content} viewMode={viewMode} />
          )}
          {block.type === 'benefits' && (
            <PreviewBenefitsBlock content={block.content} viewMode={viewMode} />
          )}
          {block.type === 'contact' && (
            <PreviewContactBlock content={block.content} viewMode={viewMode} />
          )}
          {block.type === 'testimonials' && (
            <PreviewTestimonialsBlock content={block.content} viewMode={viewMode} />
          )}
          {block.type === 'custom' && (
            <PreviewCustomBlock content={block.content} viewMode={viewMode} />
          )}
        </div>
      ))}

      {visibleBlocks.length === 0 && (
        <div className="flex items-center justify-center h-96 text-muted-foreground">
          <div className="text-center">
            <p className="text-lg mb-2">Keine sichtbaren Blöcke</p>
            <p className="text-sm">Fügen Sie Content-Blöcke hinzu und aktivieren Sie diese.</p>
          </div>
        </div>
      )}
    </div>
  );
}

function PreviewHeroBlock({ content, viewMode }: { content: any; viewMode: string }) {
  const isMobile = viewMode === 'mobile';
  
  return (
    <div className="container mx-auto px-4 text-center">
      <div className="max-w-4xl mx-auto">
        {content.title && (
          <h1 className={`font-bold mb-6 ${isMobile ? 'text-3xl' : 'text-5xl'}`}>
            {content.title.replace('{tenant_name}', 'Demo Restaurant')}
          </h1>
        )}
        
        {content.subtitle && (
          <p className={`text-muted-foreground mb-8 ${isMobile ? 'text-lg' : 'text-xl'}`}>
            {content.subtitle}
          </p>
        )}
        
        {content.buttonText && (
          <Button size={isMobile ? 'default' : 'lg'} className="px-8">
            {content.buttonText}
          </Button>
        )}
      </div>
    </div>
  );
}

function PreviewBenefitsBlock({ content, viewMode }: { content: any; viewMode: string }) {
  const benefits = content.benefits || [];
  const isMobile = viewMode === 'mobile';
  
  return (
    <div className="container mx-auto px-4">
      <div className="max-w-6xl mx-auto">
        {content.title && (
          <h2 className={`font-bold text-center mb-12 ${isMobile ? 'text-2xl' : 'text-3xl'}`}>
            {content.title.replace('{tenant_name}', 'Demo Restaurant')}
          </h2>
        )}
        
        <div className={`grid gap-8 ${
          isMobile ? 'grid-cols-1' : viewMode === 'tablet' ? 'grid-cols-2' : 'grid-cols-3'
        }`}>
          {benefits.map((benefit: any, index: number) => (
            <div key={index} className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 bg-primary rounded-full"></div>
              </div>
              <h3 className="font-semibold text-lg mb-2">{benefit.title}</h3>
              <p className="text-muted-foreground">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PreviewContactBlock({ content, viewMode }: { content: any; viewMode: string }) {
  const fields = content.fields || [];
  const isMobile = viewMode === 'mobile';
  
  return (
    <div className="container mx-auto px-4">
      <div className="max-w-2xl mx-auto">
        {content.title && (
          <h2 className={`font-bold text-center mb-4 ${isMobile ? 'text-2xl' : 'text-3xl'}`}>
            {content.title}
          </h2>
        )}
        
        {content.subtitle && (
          <p className="text-center text-muted-foreground mb-8">
            {content.subtitle}
          </p>
        )}
        
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <form className="space-y-4">
            {fields.map((field: any, index: number) => (
              <div key={index}>
                <label className="block text-sm font-medium mb-2">
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </label>
                {field.type === 'textarea' ? (
                  <textarea
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    rows={4}
                    placeholder={`${field.label} eingeben...`}
                  />
                ) : (
                  <input
                    type={field.type}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder={`${field.label} eingeben...`}
                  />
                )}
              </div>
            ))}
            
            <Button type="submit" className="w-full">
              {content.buttonText || 'Senden'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

function PreviewTestimonialsBlock({ content, viewMode }: { content: any; viewMode: string }) {
  const testimonials = content.testimonials || [];
  const isMobile = viewMode === 'mobile';
  
  return (
    <div className="container mx-auto px-4">
      <div className="max-w-4xl mx-auto text-center">
        {content.title && (
          <h2 className={`font-bold mb-12 ${isMobile ? 'text-2xl' : 'text-3xl'}`}>
            {content.title}
          </h2>
        )}
        
        {testimonials.length > 0 ? (
          <div className={`grid gap-6 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
            {testimonials.map((testimonial: any, index: number) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm">
                <div className="flex items-center justify-center mb-4">
                  {[...Array(testimonial.rating || 5)].map((_, i) => (
                    <span key={i} className="text-yellow-400">★</span>
                  ))}
                </div>
                <p className="text-muted-foreground mb-4">"{testimonial.text}"</p>
                <p className="font-semibold">- {testimonial.name}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <p className="text-muted-foreground">Testimonials werden hier angezeigt</p>
          </div>
        )}
      </div>
    </div>
  );
}

function PreviewCustomBlock({ content, viewMode }: { content: any; viewMode: string }) {
  return (
    <div className="container mx-auto px-4">
      {content.html ? (
        <div 
          className="max-w-4xl mx-auto"
          dangerouslySetInnerHTML={{ __html: sanitizeHTML(content.html) }}
        />
      ) : (
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <p className="text-muted-foreground">Custom Content wird hier angezeigt</p>
          </div>
        </div>
      )}
    </div>
  );
}